// w32 Bahor Bog'i — garden-arch wedding preview card.
//
// The full template is a lush watercolor garden invitation (cream +
// sage + blush + gold). For the catalog card we keep that palette: a
// soft cream→sage paper-fold frame, an inner ivory card, and a focal
// medallion holding a hand-drawn floral-arch motif (an arched garland
// of sage leaves + blush blossoms). Names in the template font; the
// eyebrow / footer use the standard small-caps treatment.
import { type LayoutCtx } from "./_shared";

const FRAME =
  "linear-gradient(135deg, #FDF6EE 0%, #E4ECDF 24%, #FDF6EE 50%, #EAD9D9 74%, #FDF6EE 100%)";

export function GardenArchPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const sage = "#6B8F5E";
  const blush = "#D4A0A0";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FRAME,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.38em",
            color: accent,
            marginBottom: "6%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* Garland-arch motif. */}
        <div style={{ width: "66%", aspectRatio: "1 / 1", position: "relative", marginBottom: "6%" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: FRAME,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                display: "grid",
                placeItems: "center",
                boxSizing: "border-box",
              }}
            >
              <svg viewBox="0 0 100 100" width="74%" height="74%" aria-hidden>
                {/* arched garland */}
                <path
                  d="M16 70 Q50 8 84 70"
                  fill="none"
                  stroke={sage}
                  strokeWidth="2.4"
                  strokeLinecap="round"
                />
                {/* leaves along the arch */}
                {[0.12, 0.3, 0.5, 0.7, 0.88].map((p, i) => {
                  const x = 16 + p * 68;
                  const y = 70 - Math.sin(p * Math.PI) * 56;
                  return (
                    <g key={i}>
                      <ellipse cx={x} cy={y} rx="5.4" ry="2.6"
                        fill={sage} opacity="0.85"
                        transform={`rotate(${(p - 0.5) * 120} ${x} ${y})`} />
                      <circle cx={x} cy={y} r="3.1" fill={i % 2 ? blush : accent} />
                    </g>
                  );
                })}
                {/* hanging buds at the arch ends */}
                <circle cx="16" cy="74" r="3.6" fill={blush} />
                <circle cx="84" cy="74" r="3.6" fill={blush} />
              </svg>
            </div>
          </div>
        </div>

        <div style={{ fontFamily: font, fontSize: 30, lineHeight: 1.05, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.3em",
            color: accent,
            marginTop: "4%",
          }}
        >
          {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.26em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
